/**
 * Preferences page — user self-service for notification channel handles.
 *
 * Sections:
 *   1. Slack — User ID text input + Verify button (Phase 39 / Plan 41-09;
 *      field renamed slackHandle → slackUserId in quick 260521-vct).
 *      Successful verify saves the User ID via PATCH /api/auth/preferences
 *      (per checker I-21, verified against backend/src/api/routes/auth.ts).
 *   2. Phone — E.164 number with save-on-blur for SMS notifications (Phase 60 / Plan 60-03).
 *
 * MKS-392 briefly added a third "Privacy" section — the
 * "Show my birthday to the team" switch. It is gone (Petra, staging check
 * 2026-11-02): birthdays on the dashboard "Upcoming" card reach admin and HR
 * only, so a person has nothing left to decide here. The birth date stays
 * editable on /profile, just without any visibility control.
 *
 * Phase 46 Plan 46-08 — DELETED the Phase 41 Plan 41-09 sessionStorage
 * notification routing matrix Card. The matrix is replaced by per-project
 * routes on ProjectDetailPage (admin/PM) and the global admin matrix at
 * /admin/notifications/routes (admin). The sessionStorage hook
 * `useNotificationRouting` is also removed from src/hooks/.
 *
 * D-Copy gate: English-only. NOTIF-02 (page half — bell dropdown was 41-03).
 *
 * Defense-in-depth on Slack User ID: backend re-validates the regex
 * `/^[UW][A-Z0-9]{8,18}$/` (quick 260521-vct). Frontend stub additionally
 * rejects IDs containing "admin" (Open Q4 deterministic failure path).
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../services/authContext';
import { TopBar } from '../../components/ui/TopBar';
import { Card } from '../../components/ui/Card';
import { Btn } from '../../components/ui/Btn';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { useToast } from '../../components/ui/Toast';
import {
  updateUserPreferences,
  verifySlackUserIdStub,
} from '../../services/api/userPreferences';
import { PhoneNumberField } from '../../components/profile/PhoneNumberField';

export function PreferencesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const initialUserId = user?.slackUserId ?? '';
  const [slackUserId, setSlackUserId] = useState(initialUserId);
  const [slackVerified, setSlackVerified] = useState(!!initialUserId);
  const [verifying, setVerifying] = useState(false);

  const saveUserId = useMutation({
    mutationFn: (userId: string) => updateUserPreferences({ slackUserId: userId }),
    onSuccess: () => toast('Slack User ID saved', 'success'),
    onError: () => toast('Failed to save Slack User ID', 'error'),
  });

  const handleVerify = async () => {
    const trimmed = slackUserId.trim();
    if (!trimmed) {
      toast('Enter a Slack User ID first', 'warning');
      return;
    }
    setVerifying(true);
    try {
      const ok = await verifySlackUserIdStub(trimmed);
      if (ok) {
        setSlackVerified(true);
        saveUserId.mutate(trimmed);
      } else {
        setSlackVerified(false);
        toast("Cannot verify User IDs containing 'admin'", 'error');
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <TopBar
        title="Preferences"
        breadcrumbs={[{ label: 'My account', href: '/me' }, { label: 'Preferences' }]}
      />
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <Card title="Slack">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label="Slack User ID">
                <TextInput
                  value={slackUserId}
                  onChange={(e) => {
                    setSlackUserId(e.target.value);
                    setSlackVerified(false);
                  }}
                  placeholder="e.g. U07PP72S8GJ"
                />
              </Field>
            </div>
            <Btn
              variant="outline-accent"
              size="md"
              onClick={handleVerify}
              isLoading={verifying || saveUserId.isPending}
            >
              {slackVerified ? 'Re-verify' : 'Verify'}
            </Btn>
          </div>
          {slackVerified && (
            <p className="text-[11.5px] text-emerald-700 mt-2">
              Verified. Slack delivery is now available.
            </p>
          )}
          <p className="text-[10.5px] text-ms-text-muted mt-3">
            Slack User ID verification is a stub for v7.0. Real Slack workspace lookup will
            land in v7.1.
          </p>
        </Card>

        <PhoneNumberField initialValue={user?.phoneNumber ?? null} />
      </div>
    </>
  );
}
